import { useState } from "react";
import { updateYouTubeChannelAnalytics } from "../services/outreach/integrations/youtube";
import { updateSpotifyPlaylistAnalytics } from "../services/outreach/integrations/spotify";
import { updateMusicBlogAnalytics } from "../services/outreach/integrations/similarweb";
import {
  ReactionChannel,
  PlaylistingChannel,
  SpotifyPlaylist,
  MusicBlog,
  OutreachCategory,
} from "../types/outreach";

interface UseOutreachAnalyticsReturn {
  refreshingYouTube: boolean;
  refreshingSpotify: boolean;
  refreshingMusicBlogs: boolean;
  refreshYouTubeAnalytics: () => Promise<{
    totalChannels: number;
    successCount: number;
  }>;
  refreshSpotifyAnalytics: () => Promise<{
    totalPlaylists: number;
    successCount: number;
  }>;
  refreshMusicBlogsAnalytics: () => Promise<{
    totalBlogs: number;
    successCount: number;
  }>;
}

export const useOutreachAnalytics = (
  reactionChannels: ReactionChannel[],
  playlistingChannels: PlaylistingChannel[],
  spotifyPlaylists: SpotifyPlaylist[],
  musicBlogs: MusicBlog[],
  onRefreshComplete: () => void
): UseOutreachAnalyticsReturn => {
  const [refreshingYouTube, setRefreshingYouTube] = useState<boolean>(false);
  const [refreshingSpotify, setRefreshingSpotify] = useState<boolean>(false);
  const [refreshingMusicBlogs, setRefreshingMusicBlogs] =
    useState<boolean>(false);

  const refreshYouTubeAnalytics = async (): Promise<{
    totalChannels: number;
    successCount: number;
  }> => {
    setRefreshingYouTube(true);
    let totalChannels = 0;
    let successCount = 0;

    try {
      // Process reaction channels
      console.log(`Processing ${reactionChannels.length} reaction channels...`);
      for (const channel of reactionChannels) {
        totalChannels++;
        console.log(`Updating reaction channel: ${channel.channelName}`);
        const success = await updateYouTubeChannelAnalytics(
          channel.id,
          OutreachCategory.REACTION_CHANNELS
        );
        if (success) successCount++;
      }

      // Process playlisting channels
      console.log(
        `Processing ${playlistingChannels.length} playlisting channels...`
      );
      for (const channel of playlistingChannels) {
        totalChannels++;
        console.log(`Updating playlisting channel: ${channel.channelName}`);
        const success = await updateYouTubeChannelAnalytics(
          channel.id,
          OutreachCategory.PLAYLISTING_CHANNELS
        );
        if (success) successCount++;
      }

      console.log(
        `Successfully updated ${successCount} of ${totalChannels} YouTube channels.`
      );

      // Call the callback to refresh data in the parent component
      onRefreshComplete();

      return { totalChannels, successCount };
    } catch (error) {
      console.error("Error refreshing YouTube analytics:", error);
      throw error;
    } finally {
      setRefreshingYouTube(false);
    }
  };

  const refreshSpotifyAnalytics = async (): Promise<{
    totalPlaylists: number;
    successCount: number;
  }> => {
    setRefreshingSpotify(true);
    let successCount = 0;
    const totalPlaylists = spotifyPlaylists.length;

    try {
      console.log(`Processing ${totalPlaylists} Spotify playlists...`);

      // Update each playlist
      for (const playlist of spotifyPlaylists) {
        console.log(`Updating Spotify playlist: ${playlist.playlistName}`);
        const success = await updateSpotifyPlaylistAnalytics(playlist.id);

        if (success) {
          successCount++;
          console.log(
            `Successfully updated playlist: ${playlist.playlistName}`
          );
        } else {
          console.log(`Failed to update playlist: ${playlist.playlistName}`);
        }
      }

      console.log(
        `Successfully updated ${successCount} of ${totalPlaylists} Spotify playlists.`
      );

      // Call the callback to refresh data in the parent component
      onRefreshComplete();

      return { totalPlaylists, successCount };
    } catch (error) {
      console.error("Error refreshing Spotify analytics:", error);
      throw error;
    } finally {
      setRefreshingSpotify(false);
    }
  };

  const refreshMusicBlogsAnalytics = async (): Promise<{
    totalBlogs: number;
    successCount: number;
  }> => {
    setRefreshingMusicBlogs(true);
    let successCount = 0;
    const totalBlogs = musicBlogs.length;

    try {
      console.log(`Processing ${totalBlogs} music blogs...`);

      // Update each blog
      for (const blog of musicBlogs) {
        console.log(`Updating music blog: ${blog.blogName}`);
        const success = await updateMusicBlogAnalytics(blog.id);

        if (success) {
          successCount++;
          console.log(`Successfully updated blog: ${blog.blogName}`);
        } else {
          console.log(`Failed to update blog: ${blog.blogName}`);
        }
      }

      console.log(
        `Successfully updated ${successCount} of ${totalBlogs} music blogs.`
      );

      // Call the callback to refresh data in the parent component
      onRefreshComplete();

      return { totalBlogs, successCount };
    } catch (error) {
      console.error("Error refreshing music blog analytics:", error);
      throw error;
    } finally {
      setRefreshingMusicBlogs(false);
    }
  };

  return {
    refreshingYouTube,
    refreshingSpotify,
    refreshingMusicBlogs,
    refreshYouTubeAnalytics,
    refreshSpotifyAnalytics,
    refreshMusicBlogsAnalytics,
  };
};
